import { ReactLocalization } from "@fluent/react";
import { differenceInYears } from "date-fns";
import { z } from "zod";
import { Institution } from "@lending/types";

function createValidatePhone(l10n: ReactLocalization) {
  return z
    .string()
    .refine(
      (val) => {
        return val.trim().length > 0;
      },
      {
        message: l10n.getString("error-required", null, "Required"),
      }
    )
    .refine(
      (val) => {
        const phoneDigits = val.replace(/[^0-9]/g, "");

        const normalizedDigits = phoneDigits.startsWith("1")
          ? phoneDigits.substring(1)
          : phoneDigits;

        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(normalizedDigits);
      },
      {
        message: l10n.getString("error-invalid-phone", null, "Invalid phone number"),
      }
    );
}

function createValidateDateOfBirth(l10n: ReactLocalization, institution: Institution) {
  return z
    .string()
    .refine(
      (val) => {
        return val.trim().length > 0;
      },
      {
        message: l10n.getString("error-required", null, "Required"),
      }
    )
    .refine((val) => {
      const [year, month, day] = val.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    })
    .refine(
      (val) => {
        const { lending_minimum_applicant_birth_year } = institution;
        const year = Number(val.slice(0, 4));
        return year >= lending_minimum_applicant_birth_year;
      },
      {
        message: l10n.getString(
          "error-invalid-applicant-birth-year",
          { min_birth_year: institution.lending_minimum_applicant_birth_year },
          "Birth year must be above institution minimum"
        ),
      }
    )
    .refine(
      (val) => {
        const { lending_minimum_applicant_age } = institution;
        const [year, month, day] = val.split("-").map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        const age = differenceInYears(today, birthDate);
        return age >= lending_minimum_applicant_age;
      },
      {
        message: l10n.getString(
          "error-invalid-applicant-age",
          { min_age: institution.lending_minimum_applicant_age },
          `Must be at least ${institution.lending_minimum_applicant_age} years old`
        ),
      }
    );
}

function createValidateSSN(l10n: ReactLocalization) {
  return z
    .string()
    .refine(
      (val) => {
        return val.trim().length > 0;
      },
      {
        message: l10n.getString("error-required", null, "Required"),
      }
    )
    .refine(
      (val) => {
        // Accept masked format (***-**-1234) or valid 9-digit SSN
        // We need to accept the masked format because that's what is prefilled if the user returns to the page.
        const maskedPattern = /^\*{3}-\*{2}-\d{4}$/;
        if (maskedPattern.test(val)) return true;

        const ssnDigits = val.replace(/[^0-9]/g, "");
        const ssnRegex = /^\d{9}$/;
        return ssnRegex.test(ssnDigits);
      },
      {
        message: l10n.getString("error-invalid-ssn", null, "Invalid SSN"),
      }
    );
}

function createValidateZipCode(l10n: ReactLocalization) {
  return z
    .string()
    .refine(
      (val) => {
        return val.trim().length > 0;
      },
      {
        message: l10n.getString("error-required", null, "Required"),
      }
    )
    .refine(
      (val) => {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return zipRegex.test(val);
      },
      {
        message: l10n.getString("error-invalid-zip", null, "Invalid ZIP code"),
      }
    );
}

export { createValidatePhone, createValidateDateOfBirth, createValidateSSN, createValidateZipCode };
